import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/layout/AppLayout';
import { dummyData } from './data';
import ProgressOverview from './ProgressOverview';
import StageSnapshot from './StageSnapshot';
import DelayedTasks from './DelayedTasks';
import Accountability from './Accountability';
import MaterialStatus from './MaterialStatus';
import NextWeekPlan from './NextWeekPlan';
import PhotoHighlights from './PhotoHighlights';
import { ArrowLeft } from 'lucide-react';
import DownloadPdfButton from '../DownloadPdfButton';
import useOperationsReportPdf from '../useOperationsReportPdf';

const getSampleData = () => {
  const progressStats = {
    planned: dummyData.weeklyStats.planned,
    done: dummyData.weeklyStats.done,
    delayed: dummyData.weeklyStats.delayed,
    completion: dummyData.weeklyStats.completion
  };

  const stagesData = dummyData.stages;

  const delayedTasks = dummyData.topDelays.map((d, i) => ({
    id: d.id,
    title: d.title,
    cause: d.cause,
    workType: d.type || 'General',
    status: 'Pending',
    person: d.person,
    type: d.type,
    color: dummyData.stageColors?.[i % dummyData.stageColors.length] || '#FF7676'
  }));

  const accountabilityData = dummyData.accountability;

  const photoHighlightsData = dummyData.photoHighlights.slice(0, 8);

  const nextWeekPlanData = dummyData.nextWeekPlan.map((p, i) => ({
    ...p,
    color: dummyData.stageColors?.[i % dummyData.stageColors.length] || '#D4B95E'
  }));

  // Map materialSignals to MaterialStatus shape: itemName, stock_criticality, estimated, used, unit
  const statusToCriticality = { Normal: 100, Shortage: 80, Low: 90, Critical: 70, Excess: 150 };
  const materialsData = dummyData.materialSignals.map((m) => {
    const parts = (m.count || '').split('/');
    const used = parseInt(parts[0], 10) || 0;
    const estimated = parseInt(parts[1], 10) || 0;
    return {
      itemName: m.name,
      stock_criticality: statusToCriticality[m.status] ?? 100,
      estimated: estimated || 100,
      used: used || 0,
      unit: (m.count || '').includes('Bags') ? 'Bags' : (m.count || '').includes('Tons') ? 'Tons' : (m.count || '').includes('m3') ? 'm3' : (m.count || '').includes('Units') ? 'Units' : 'Bundles'
    };
  });

  return {
    progressStats,
    stagesData,
    delayedTasks,
    accountabilityData,
    photoHighlightsData,
    nextWeekPlanData,
    materialsData
  };
};

const SampleWeeklyReport = () => {
  const navigate = useNavigate();
  const { downloadSamplePdf, isDownloadingPdf } = useOperationsReportPdf('weekly');
  const data = getSampleData();

  return (
    <Layout>
      <div className="bg-[#F8FAFC] min-h-screen">
        <div className="sticky top-0 z-40 bg-[#EEF3F7] backdrop-blur pt-6 px-6 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/builder/operations/weekly-reports')}
                className="flex items-center gap-2 px-3 py-2 text-[#526477] hover:bg-gray-100 rounded-md transition-colors text-sm font-medium"
              >
                <ArrowLeft size={20} />
                Back to Weekly Reports
              </button>
              <span className="h-5 w-px bg-[#526477] opacity-50" />
              <h1 className="text-lg font-bold text-[#111827]">Sample Weekly Report</h1>
              <span className="px-2 py-0.5 bg-[#1F4B55] text-white text-xs font-semibold rounded">Preview</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-[#526477] font-medium">
                Project: {dummyData.projectName} · Week: {dummyData.date}
              </div>
              <DownloadPdfButton onClick={downloadSamplePdf} isDownloading={isDownloadingPdf} />
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 max-w-[1600px] mx-auto">
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2 flex flex-col gap-6">
                <div>
                  <ProgressOverview stats={data.progressStats} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <DelayedTasks tasks={data.delayedTasks} />
                  </div>
                  <div>
                    <MaterialStatus materials={data.materialsData} />
                  </div>
                </div>
                <div>
                  <NextWeekPlan plans={data.nextWeekPlanData} />
                </div>
              </div>
              <div className="flex flex-col gap-6 lg:mt-0">
                <div>
                  <StageSnapshot stages={data.stagesData} />
                </div>
                <div>
                  <Accountability people={data.accountabilityData} />
                </div>
                <div>
                  <PhotoHighlights photos={data.photoHighlightsData} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SampleWeeklyReport;
